// Master-detail Actions Toolkit page. Single route at /toolkit/actions —
// every CRUD flow happens inside drawers + modals so list context is never
// lost. The earlier separate routes (/create, /:uuid, /:uuid/versions/new)
// were removed; this page replaces all three.

import React, { useState } from 'react';
import { t } from '@lingui/macro';

import Helmet from '../../components/SEO/Helmet';
import { PageToolbar, ListPane } from './ListPane';
import DetailPane, { EmptyDetail, NoSelection } from './DetailPane';
import { OverflowMenu, ToastTray } from './ActionModals';
import ActionModalRouter from './ActionModalRouter';
import SkeletonView from './SkeletonView';
import useActionIntegrations from './useActionIntegrations';
import useActionsCatalog from './useActionsCatalog';
import useActionsCrud from './useActionsCrud';
import useActionsNavigation from './useActionsNavigation';
import useToasts from './useToasts';
import { currentVersion } from './atUtils';
import './actions-toolkit.css';

const OVERFLOW_MENU_WIDTH = 180;
const OVERFLOW_MENU_GAP = 6;

export default function ActionsPage() {
  const [modal, setModal] = useState(null);
  const { toasts, pushToast } = useToasts();

  const { actions, setActions, loading, fetchAll, refreshKey } =
    useActionsCatalog({ pushToast });

  const nav = useActionsNavigation(actions);
  const { action, version } = nav;

  useActionIntegrations({ action, version, refreshKey, setActions });

  const crud = useActionsCrud({
    actions,
    setActions,
    action,
    version,
    fetchAll,
    pushToast,
    setModal,
    onCreated: nav.selectAction,
  });

  if (loading) return <SkeletonView />;

  return (
    <div className="at-root">
      <Helmet title={t`Actions`} description={t`Actions`} />

      <div className="page">
        <PageToolbar
          filter={nav.filter}
          onFilter={nav.setFilter}
          onCreate={() => setModal({ kind: 'create' })}
          onImport={() => setModal({ kind: 'import' })}
        />

        <div className="body">
          <ListPane
            actions={actions}
            selectedId={nav.selectedId}
            onSelect={nav.selectAction}
            filter={nav.filter}
            onFilter={nav.setFilter}
          />
          {actions.length === 0 && (
            <EmptyDetail onCreate={() => setModal({ kind: 'create' })} />
          )}
          {actions.length > 0 && (!action || !version) && <NoSelection />}
          {actions.length > 0 && action && version && (
            <DetailPane
              action={action}
              version={version}
              tab={nav.tab}
              onTabChange={nav.setTab}
              onVersionChange={nav.setSelectedVersion}
              onEdit={() => setModal({ kind: 'edit' })}
              onCopy={crud.copyActionExport}
              onDownload={crud.downloadActionExport}
              onMenu={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                nav.setMenuPos({
                  x: r.right - OVERFLOW_MENU_WIDTH,
                  y: r.bottom + OVERFLOW_MENU_GAP,
                });
              }}
              onMigrateFromVersion={(fromV) => {
                const fromVRef = action.versions.find(
                  (v) => v.version === fromV,
                );
                setModal({
                  kind: 'migrate',
                  actionId: action.id,
                  fromVersion: fromV,
                  toVersion: currentVersion(action).version,
                  integrationsOnFrom: fromVRef?.integrations || [],
                });
              }}
              onCompare={(fromV, toV) =>
                setModal({
                  kind: 'diff',
                  actionId: action.id,
                  fromVersion: fromV,
                  toVersion: toV,
                })
              }
            />
          )}
        </div>
      </div>

      <ActionModalRouter
        modal={modal}
        setModal={setModal}
        actions={actions}
        action={action}
        version={version}
        crud={crud}
      />

      {nav.menuPos && action && version && (
        <OverflowMenu
          x={nav.menuPos.x}
          y={nav.menuPos.y}
          version={version}
          onLock={() => {
            nav.setMenuPos(null);
            setModal({ kind: 'lock' });
          }}
          onUnlock={() => {
            nav.setMenuPos(null);
            setModal({ kind: 'lock' });
          }}
          onDelete={() => {
            nav.setMenuPos(null);
            setModal({ kind: 'delete' });
          }}
          onClose={() => nav.setMenuPos(null)}
        />
      )}

      <ToastTray toasts={toasts} />
    </div>
  );
}
